import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Checkbox,
  Stack,
  Divider,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import Loader from "../../../Components/Loader";
import { Colors } from "../../../Styles/Colors";
import CommonValidation from "../../../Components/CommonFunctions/CommonValidation";
import { CurrencySymbol, TXN_MSG_MAX, TXN_MSG_MAX_LEN } from "../../../Utility/Constants";
import { ImagePaths } from "../../../Utility/ImagePaths";

const DATE_FORMAT = "YYYY-MM-DD";
const DATE_TIME_API = "YYYY-MM-DD HH:mm:ss";

// If date is inside any slot: Cleaning = cleaning (blue), Patient/Maintenance/other = booked (red). Else = available (green).
// When modifyReservation is set: that reservation's bed + date range is ALWAYS available (by bed_id and start/end dates), so user can pick any range.
// Other slots (other patients, cleaning, maintenance) stay as usual.
function getBedDateStatus(bed, dateStr, modifyReservation) {
  const check = dayjs(dateStr).format(DATE_FORMAT);
  // Modify flow: this reservation's slot (same bed, date within reservation range) is always available
  if (modifyReservation && bed?.bed_id != null && String(bed.bed_id) === String(modifyReservation.bed_id)) {
    const resStart = dayjs(modifyReservation.start_date, ["DD-MM-YYYY hh:mm A", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"]);
    const resEnd = dayjs(modifyReservation.end_date, ["DD-MM-YYYY hh:mm A", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"]);
    if (resStart.isValid() && resEnd.isValid()) {
      const resStartDay = resStart.format(DATE_FORMAT);
      const resEndDay = resEnd.format(DATE_FORMAT);
      if (check >= resStartDay && check <= resEndDay) return "available";
    }
  }
  const data = bed?.data || [];
  for (const item of data) {
    const start = item.start_date ? dayjs(item.start_date, ["DD-MM-YYYY hh:mm A", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"]) : null;
    const end = item.end_date ? dayjs(item.end_date, ["DD-MM-YYYY hh:mm A", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"]) : null;
    if (!start || !end) continue;
    const startDay = start.format(DATE_FORMAT);
    const endDay = end.format(DATE_FORMAT);
    if (check >= startDay && check <= endDay) {
      if (modifyReservation && (item.reservation_id == modifyReservation.id || item.reserve_id == modifyReservation.id)) return "available";
      if (item.for_cleaning === true || item.allocated_for === "Cleaning") return "cleaning";
      if (item.allocated_for === "Patient") return "booked";
      if (item.allocated_for === "Maintenance") return "maintenance";
      return "booked";
    }
  }
  return "available";
}

// Get consecutive available range between two indices (for drag)
function getConsecutiveRangeBetween(bed, dates, startStr, endStr, modifyReservation) {
  const si = dates.findIndex((d) => d === startStr);
  const ei = dates.findIndex((d) => d === endStr);
  if (si === -1 || ei === -1) return null;
  const low = Math.min(si, ei);
  const high = Math.max(si, ei);
  for (let i = low; i <= high; i++) {
    if (getBedDateStatus(bed, dates[i], modifyReservation) !== "available") return null;
  }
  return { start: dates[low], end: dates[high] };
}

const TODAY = () => dayjs().startOf("day");

// Month-wise: if current month -> 31 days from today (today to today+30). If future month -> 1st to last.
function getMonthWiseRange(monthDayjs) {
  const today = TODAY();
  const startOfMonth = monthDayjs.startOf("month");
  const endOfMonth = monthDayjs.endOf("month");
  if (monthDayjs.isSame(today, "month")) {
    return { from: today, to: today.add(30, "day") }; // 31 days including today
  }
  return { from: startOfMonth, to: endOfMonth };
}

const initialMonthRange = getMonthWiseRange(TODAY());

function getInitialRange(modifyRow) {
  if (!modifyRow?.start_date || !modifyRow?.end_date) return initialMonthRange;
  const start = dayjs(modifyRow.start_date, ["DD-MM-YYYY hh:mm A", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"]);
  const end = dayjs(modifyRow.end_date, ["DD-MM-YYYY hh:mm A", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"]);
  if (start.isValid() && end.isValid()) return { from: start.startOf("day"), to: end.add(31, "day").startOf("day") };
  return initialMonthRange;
}

export default function RoomBooking(props) {
  const locationState = props?.history?.location?.state ?? {};
  const modifyFromLocation = locationState?.modifyReservation ?? null;
  const fromAdmissionDetails = locationState?.fromAdmissionDetails === true;
  const admissionPatient = locationState?.patientDetails ?? null;
  const admissionDoctorId = locationState?.doctorId ?? null;
  const returnPath = locationState?.returnPath ?? null;
  const [modifyReservation, setModifyReservation] = useState(modifyFromLocation);
  const initialRange = getInitialRange(modifyFromLocation);
  const [dateFilterType, setDateFilterType] = useState(modifyFromLocation ? "custom" : "monthly"); // 'monthly' | 'custom'
  const [selectedMonth, setSelectedMonth] = useState(TODAY()); // for month-wise
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [bookingData, setBookingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [blockList, setBlockList] = useState([]);
  const [floorList, setFloorList] = useState([]);
  const [wardList, setWardList] = useState([]);
  const [roomList, setRoomList] = useState([]);
  const [roomTypeList, setRoomTypeList] = useState([]);
  const [filterBlock, setFilterBlock] = useState("");
  const [filterFloor, setFilterFloor] = useState("");
  const [filterWard, setFilterWard] = useState("");
  const [filterRoom, setFilterRoom] = useState("");
  const [filterRoomType, setFilterRoomType] = useState("");
  const [selection, setSelection] = useState(null); // { bed, bedId, startDate, endDate }
  const [dragState, setDragState] = useState(null); // { bed, startDateStr, endDateStr } while dragging
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [allocatedFor, setAllocatedFor] = useState("Patient");
  const [patientSearchKey, setPatientSearchKey] = useState("");
  const [patientList, setPatientList] = useState([]);
  const [patientSearchOpen, setPatientSearchOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [doctorsList, setDoctorsList] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [remarks, setRemarks] = useState("");
  const [paymentMode, setPaymentMode] = useState({ cash: false, card: false, upi: false, bank: false });
  const [amounts, setAmounts] = useState({
    cashAmount: 0, cardAmount: 0, upiAmount: 0, bankAmount: 0,
    cardNumber: "", upiNumber: "", bankNumber: "",
  });
  const [errorMsg, setErrorMsg] = useState(false);
  const [errorMsgText, setErrorMsgText] = useState("");
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState("");
  const defaultBlockSetRef = useRef(false);
  const searchDebounceRef = useRef(null);
  const modifyInitDoneRef = useRef(false);

  const fromStr = fromDate.format(DATE_FORMAT);
  const toStr = toDate.format(DATE_FORMAT);
  const dateRange = useMemo(() => {
    const arr = [];
    let d = dayjs(fromStr);
    const end = dayjs(toStr);
    while (!d.isAfter(end)) {
      arr.push(d.format(DATE_FORMAT));
      d = d.add(1, "day");
    }
    return arr;
  }, [fromStr, toStr]);

  const fetchBookingDetails = useCallback(() => {
    setLoading(true);
    RestAPIService.getAll(`${Serviceurls.IP_ROOM_BOOKING_DETAILS}?from_date=${fromStr}&to_date=${toStr}`)
      .then((res) => {
        if (res?.data?.status === "success") {
          const data = res?.data?.data || [];
          setBookingData(data);
          const blocks = [...new Set(data.map((b) => b.block_name).filter(Boolean))];
          const floors = [...new Set(data.map((b) => b.floor).filter(Boolean))];
          const wards = [...new Set(data.map((b) => b.ward_name).filter(Boolean))];
          const rooms = [...new Set(data.map((b) => b.room_number).filter(Boolean))];
          const types = [...new Set(data.map((b) => b.room_type).filter(Boolean))];
          setBlockList(blocks);
          setFloorList(floors);
          setWardList(wards);
          setRoomList(rooms);
          setRoomTypeList(types);
          if (!defaultBlockSetRef.current && blocks.length) {
            setFilterBlock(blocks[0]);
            defaultBlockSetRef.current = true;
          }
        } else {
          setErrorMsgText(res?.data?.message || "Failed to load");
          setErrorMsg(true);
        }
      })
      .catch((e) => {
        setErrorMsgText(e?.response?.data?.message || e?.message || "Error");
        setErrorMsg(true);
      })
      .finally(() => setLoading(false));
  }, [fromStr, toStr, filterBlock]);

  useEffect(() => {
    fetchBookingDetails();
  }, [fetchBookingDetails]);

  // From Admission Details: pre-fill patient and doctor
  useEffect(() => {
    if (!fromAdmissionDetails || !admissionPatient) return;
    const patient = {
      patient_id: admissionPatient.patient_id,
      patient_name: admissionPatient.patient_name || admissionPatient.first_name,
      patient_account_number: admissionPatient.patient_account_number,
      patient_age: admissionPatient.patient_age,
      patient_gender: admissionPatient.patient_gender || admissionPatient.gender,
      mobile_number: admissionPatient.mobile_number || admissionPatient.patient__mobile_number,
    };
    setSelectedPatient(patient);
    setPatientSearchKey(patient.patient_name || patient.patient_account_number || "");
    if (admissionDoctorId) setSelectedDoctor(String(admissionDoctorId));
  }, [fromAdmissionDetails, admissionPatient, admissionDoctorId]);

  // After booking data loaded in modify mode: set selection (current bed + dates) and pre-fill patient/doctor
  useEffect(() => {
    if (!modifyReservation || !bookingData?.length || modifyInitDoneRef.current) return;
    const bed = bookingData.find((b) => String(b.bed_id) === String(modifyReservation.bed_id));
    const start = dayjs(modifyReservation.start_date, ["DD-MM-YYYY hh:mm A", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"]);
    const end = dayjs(modifyReservation.end_date, ["DD-MM-YYYY hh:mm A", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"]);
    if (bed && start.isValid() && end.isValid()) {
      const startStr = start.format(DATE_FORMAT);
      const endStr = end.format(DATE_FORMAT);
      setSelection({ bed, bedId: bed.bed_id, startDate: startStr, endDate: endStr });
      const patient = {
        patient_id: modifyReservation.patient_id,
        patient_name: modifyReservation.patient_name || modifyReservation.patient__name,
        patient_account_number: modifyReservation.patient_account_number || modifyReservation.uhid,
        patient_age: modifyReservation.patient_age,
        patient_gender: modifyReservation.patient_gender,
        mobile_number: modifyReservation.mobile_number,
      };
      setSelectedPatient(patient);
      setPatientSearchKey(patient.patient_name || patient.patient_account_number || "");
      setSelectedDoctor(modifyReservation.doctor_id ? String(modifyReservation.doctor_id) : "");
      setRemarks(modifyReservation.remarks || "");
      modifyInitDoneRef.current = true;
    }
  }, [modifyReservation, bookingData]);

  const filteredBeds = useMemo(() => {
    return bookingData.filter((b) => {
      if (filterBlock && b.block_name !== filterBlock) return false;
      if (filterFloor && String(b.floor) !== String(filterFloor)) return false;
      if (filterWard && b.ward_name !== filterWard) return false;
      if (filterRoom && String(b.room_number) !== String(filterRoom)) return false;
      if (filterRoomType && b.room_type !== filterRoomType) return false;
      return true;
    });
  }, [bookingData, filterBlock, filterFloor, filterWard, filterRoom, filterRoomType]);

  const setMonthlyRange = () => {
    setDateFilterType("monthly");
  };

  const setCustomRange = () => {
    setDateFilterType("custom");
  };

  useEffect(() => {
    if (dateFilterType === "monthly") {
      const range = getMonthWiseRange(selectedMonth);
      setFromDate(range.from);
      setToDate(range.to);
    }
  }, [dateFilterType, selectedMonth]);

  const handleMonthChange = (d) => {
    if (!d) return;
    setSelectedMonth(d);
    const range = getMonthWiseRange(d);
    setFromDate(range.from);
    setToDate(range.to);
  };

  const handleFromDateChange = (d) => {
    const newFrom = d.startOf("day");
    setFromDate(newFrom);
    if (dayjs(toDate).isBefore(newFrom) || dayjs(toDate).isSame(newFrom, "day")) {
      setToDate(newFrom.add(30, "day"));
    }
  };

  const handleToDateChange = (d) => {
    const newTo = d.startOf("day");
    if (newTo.isBefore(fromDate)) {
      setToDate(dayjs(fromDate).add(30, "day"));
    } else {
      setToDate(newTo);
    }
  };

  const handleCellMouseDown = (bed, dateStr) => {
    if (getBedDateStatus(bed, dateStr, modifyReservation) !== "available") return;
    setDragState({ bed, startDateStr: dateStr, endDateStr: dateStr });
  };

  const handleCellMouseEnter = (bed, dateStr) => {
    if (!dragState || dragState.bed?.bed_id !== bed?.bed_id) return;
    if (getBedDateStatus(bed, dateStr, modifyReservation) !== "available") return;
    setDragState((prev) => ({ ...prev, endDateStr: dateStr }));
  };

  const handleCellMouseUp = (bed, dateStr) => {
    if (!dragState) return;
    const range = getConsecutiveRangeBetween(dragState.bed, dateRange, dragState.startDateStr, dragState.endDateStr, modifyReservation);
    setDragState(null);
    if (range) {
      setSelection({
        bed: dragState.bed,
        bedId: dragState.bed.bed_id,
        startDate: range.start,
        endDate: range.end,
      });
    }
  };

  const dragStateRef = useRef(null);
  dragStateRef.current = dragState;
  useEffect(() => {
    const onMouseUp = () => {
      const state = dragStateRef.current;
      if (state) {
        const range = getConsecutiveRangeBetween(state.bed, dateRange, state.startDateStr, state.endDateStr, modifyReservation);
        setDragState(null);
        if (range) {
          setSelection({
            bed: state.bed,
            bedId: state.bed.bed_id,
            startDate: range.start,
            endDate: range.end,
          });
        }
      }
    };
    window.addEventListener("mouseup", onMouseUp);
    return () => window.removeEventListener("mouseup", onMouseUp);
  }, [dateRange, modifyReservation]);

  const displaySelection = dragState || selection;
  const isSelected = (bedId, dateStr) => {
    const src = displaySelection;
    const srcBedId = src?.bedId ?? src?.bed?.bed_id;
    if (!src || srcBedId !== bedId) return false;
    const startStr = src.startDate ?? src.startDateStr;
    const endStr = src.endDate ?? src.endDateStr;
    if (!startStr || !endStr) return false;
    const d = dayjs(dateStr);
    const s = dayjs(startStr);
    const e = dayjs(endStr);
    return !d.isBefore(s) && !d.isAfter(e);
  };

  const canBookRoom = !!selection;

  const openDrawer = () => {
    if (!canBookRoom) return;
    setDrawerOpen(true);
    if (!modifyReservation && !fromAdmissionDetails) {
      setSelectedPatient(null);
      setPatientSearchKey("");
      setSelectedDoctor("");
      setRemarks("");
    }
    setPaymentMode({ cash: false, card: false, upi: false, bank: false });
    setAmounts({ cashAmount: 0, cardAmount: 0, upiAmount: 0, bankAmount: 0, cardNumber: "", upiNumber: "", bankNumber: "" });
  };

  const fetchDoctors = useCallback(() => {
    RestAPIService.getAll(Serviceurls.FO_CONFIG_OPDOCTORNAME)
      .then((res) => {
        if (res?.data?.status === "success")
          setDoctorsList((res?.data?.data || []).filter((i) => i.specialization_active === true));
      })
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (drawerOpen && allocatedFor === "Patient") fetchDoctors();
  }, [drawerOpen, allocatedFor, fetchDoctors]);

  const patientSearch = useCallback((key) => {
    const trimmed = CommonValidation.removeSpace(key || patientSearchKey);
    if (!trimmed) {
      setPatientList([]);
      setPatientSearchOpen(false);
      return;
    }
    RestAPIService.getAll(Serviceurls.FO_PATIENT_SEARCH + `?search_key=${trimmed}&address_key=&father_husband_key=`)
      .then((res) => {
        if (res?.data?.status === "success") {
          const data = (res?.data?.data || []).map((item) => ({
            ...item,
            patient_name: item.patient_name || [item.first_name, item.last_name].filter(Boolean).join(" "),
          }));
          setPatientList(data);
          setPatientSearchOpen(true);
        }
      })
      .catch(() => setPatientList([]));
  }, [patientSearchKey]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!patientSearchKey?.trim()) {
      setPatientList([]);
      setPatientSearchOpen(false);
      return;
    }
    searchDebounceRef.current = setTimeout(() => patientSearch(patientSearchKey), 300);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [patientSearchKey, patientSearch]);

  const selectPatient = (p) => {
    const name = p.patient_name || [p.first_name, p.last_name].filter(Boolean).join(" ");
    setSelectedPatient({ ...p, patient_name: name });
    setPatientSearchKey(p?.patient_account_number || p?.uhid || "");
    setPatientSearchOpen(false);
  };

  const formatPatientOption = (p) => {
    const name = p.patient_name || [p.first_name, p.last_name].filter(Boolean).join(" ");
    const age = p.patient_age || p.age || "";
    const gender = (p.patient_gender || p.gender || "").toUpperCase();
    const g = gender === "M" ? "M" : gender === "F" ? "F" : "T";
    const line2 = [p.patient_account_number, p.mobile_number].filter(Boolean).join(" | ");
    return { name: `${name} (${age}/${g})`, line2 };
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    if (value === "" || CommonValidation.DecimalNumber(value))
      setAmounts((prev) => ({ ...prev, [name]: value }));
  };

  const handlePaymentMode = (key) => {
    setPaymentMode((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const validateAndSave = () => {
    const isPatient = allocatedFor === "Patient";
    const isModify = !!modifyReservation;
    if (isPatient) {
      if (!selectedPatient?.patient_id) {
        setErrorMsgText("Please select a patient");
        setErrorMsg(true);
        return;
      }
      if (!isModify && !selectedDoctor) {
        setErrorMsgText("Please select a doctor");
        setErrorMsg(true);
        return;
      }
      const anyMode = paymentMode.cash || paymentMode.card || paymentMode.upi || paymentMode.bank;
      if (anyMode) {
        const total = (+amounts.cashAmount || 0) + (+amounts.cardAmount || 0) + (+amounts.upiAmount || 0) + (+amounts.bankAmount || 0);
        if (total <= 0) {
          setErrorMsgText("Enter amount for selected payment mode(s)");
          setErrorMsg(true);
          return;
        }
        if ((amounts.cardAmount && (!amounts.cardNumber || amounts.cardNumber?.length < TXN_MSG_MAX_LEN)) ||
          (amounts.bankAmount && (!amounts.bankNumber || amounts.bankNumber?.length < TXN_MSG_MAX_LEN)) ||
          (amounts.upiAmount && (!amounts.upiNumber || amounts.upiNumber?.length < TXN_MSG_MAX_LEN))) {
          setErrorMsgText(TXN_MSG_MAX);
          setErrorMsg(true);
          return;
        }
      }
    }

    const startDateTime = dayjs(selection.startDate).startOf("day").format(DATE_TIME_API); // 00:00:00
    const endDateTime = dayjs(selection.endDate).hour(23).minute(59).second(59).format(DATE_TIME_API); // 23:59:59

    const payload = {
      allocated_for: isPatient ? "Patient" : allocatedFor === "House Keeping" ? "Cleaning" : "Maintenance",
      maintenance_id: null,
      reservation_id: isModify ? modifyReservation.id : null,
      modify: isModify,
      patient_id: isPatient ? selectedPatient.patient_id : null,
      start_date: startDateTime,
      end_date: endDateTime,
      bed_id: selection.bedId,
      is_attender: false,
      doctor_id: selectedDoctor || null,
      amount_received: isPatient ? ((+amounts.cashAmount || 0) + (+amounts.cardAmount || 0) + (+amounts.upiAmount || 0) + (+amounts.bankAmount || 0)) : 0,
      payment_mode: isPatient && (paymentMode.cash || paymentMode.card || paymentMode.upi || paymentMode.bank)
        ? [
          ...(paymentMode.cash ? [1] : []),
          ...(paymentMode.card ? [2] : []),
          ...(paymentMode.upi ? [3] : []),
          ...(paymentMode.bank ? [4] : []),
        ]
        : [],
      amount_cash: isPatient ? +amounts.cashAmount || 0 : 0,
      amount_card: isPatient ? +amounts.cardAmount || 0 : 0,
      upi_amount: isPatient ? +amounts.upiAmount || 0 : 0,
      bank_transfer_amount: isPatient ? +amounts.bankAmount || 0 : 0,
      cheque_amount: 0,
      card_ref_no: amounts.cardNumber || "",
      upi_ref_no: amounts.upiNumber || "",
      bank_ref_no: amounts.bankNumber || "",
      cheque_ref_no: "",
      receipt_id: null,
      receipt_for: "Advance",
      status: "Reserved",
      remarks: remarks,
    };

    setLoading(true);
    RestAPIService.create(payload, Serviceurls.IP_PATIENT_RESERVE_ROOM)
      .then((res) => {
        if (res?.data?.status === "success") {
          setSuccessMsgText(res?.data?.message || (modifyReservation ? "Reservation updated successfully" : "Room booked successfully"));
          setSuccessMsg(true);
          setDrawerOpen(false);
          if (fromAdmissionDetails && returnPath && selection?.bed) {
            const created = res?.data?.data;
            const reservationPayload = {
              id: created?.id ?? created?.reservation_id,
              reservation_id: created?.id ?? created?.reservation_id,
              bed_id: selection.bedId,
              ward_id: selection.bed?.ward_id,
              block_no_id: selection.bed?.block_id,
              block_id: selection.bed?.block_id,
              floor_number: selection.bed?.floor,
              floor: selection.bed?.floor,
              room_number: selection.bed?.room_number,
              room: selection.bed?.room_number,
              start_date: res?.data?.start_date ?? null,
              end_date: res?.data?.end_date ?? null,
            };
            const patientDetailsForReturn = selectedPatient
              ? { ...selectedPatient, patient_name: selectedPatient.patient_name || selectedPatient.first_name }
              : admissionPatient;
            props.history.push(returnPath, {
              fromRoomBookingWithReservation: true,
              reservation: reservationPayload,
              patientDetails: patientDetailsForReturn,
              appointment_id: locationState?.appointment_id,
              recommendation_id: locationState?.recommendation_id,
              ip_request_id: locationState?.ip_request_id,
              consultant_id: locationState?.consultant_id,
            });
          } else {
            setSelection(null);
            if (modifyReservation) {
              setModifyReservation(null);
              modifyInitDoneRef.current = false;
              if (props?.history?.replace) props.history.replace({ pathname: "/IPHome/RoomBooking", state: {} });
            }
            fetchBookingDetails();
          }
        } else {
          setErrorMsgText(res?.data?.message || "Failed to book");
          setErrorMsg(true);
        }
      })
      .catch((e) => {
        setErrorMsgText(e?.response?.data?.message || e?.message || "Error");
        setErrorMsg(true);
      })
      .finally(() => setLoading(false));
  };

  const msgClose = () => {
    setErrorMsg(false);
    setSuccessMsg(false);
    setErrorMsgText("");
    setSuccessMsgText("");
  };

  return (
    <Box sx={{ height: "80vh", width: "100%", backgroundColor: Colors.Background, p: 1 }}>
      <Box display="flex" justifyContent="space-between" flexWrap="wrap" alignItems="center" gap={1} mb={1}>
        <Box><Typography variant="subtitle1" fontWeight={600}>Room Booking</Typography></Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Button size="small" variant={dateFilterType === "monthly" ? "contained" : "outlined"} onClick={setMonthlyRange}>Month wise</Button>
          <Button size="small" variant={dateFilterType === "custom" ? "contained" : "outlined"} onClick={setCustomRange}>Custom</Button>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {dateFilterType === "monthly" ? (
              <DatePicker
                label="Month"
                value={selectedMonth}
                onChange={handleMonthChange}
                views={["year", "month"]}
                minDate={TODAY()}
                inputFormat="MMMM YYYY"
                renderInput={(params) => <TextField {...params} size="small" sx={{ width: 160 }} />}
              />
            ) : (
              <>
                <DatePicker label="From" value={fromDate} onChange={handleFromDateChange} minDate={TODAY()} inputFormat="DD-MM-YYYY" renderInput={(params) => <TextField {...params} size="small" sx={{ width: 140 }} />} />
                <DatePicker label="To" value={toDate} onChange={handleToDateChange} minDate={fromDate} inputFormat="DD-MM-YYYY" renderInput={(params) => <TextField {...params} size="small" sx={{ width: 140 }} />} />
              </>
            )}
          </LocalizationProvider>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select value={filterBlock} onChange={(e) => setFilterBlock(e.target.value)} displayEmpty>
              <MenuItem value="">All Block</MenuItem>
              {blockList.map((b) => (
                <MenuItem key={b} value={b}>{b}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select value={filterFloor} onChange={(e) => setFilterFloor(e.target.value)} displayEmpty>
              <MenuItem value="">All Floor</MenuItem>
              {floorList.map((f) => (
                <MenuItem key={f} value={f}>{f}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select value={filterWard} onChange={(e) => setFilterWard(e.target.value)} displayEmpty>
              <MenuItem value="">All Ward</MenuItem>
              {wardList.map((w) => (
                <MenuItem key={w} value={w}>{w}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select value={filterRoom} onChange={(e) => setFilterRoom(e.target.value)} displayEmpty>
              <MenuItem value="">All Room</MenuItem>
              {roomList.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <Select value={filterRoomType} onChange={(e) => setFilterRoomType(e.target.value)} displayEmpty>
              <MenuItem value="">All Type</MenuItem>
              {roomTypeList.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" variant="contained" color="primary" disabled={!canBookRoom} onClick={openDrawer}>BOOK ROOM</Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "end", alignItems: "center", gap: 2, mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#4CAF50" }} />
          <Typography variant="caption">Available</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#f44336" }} />
          <Typography variant="caption">Booked</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#2196F3" }} />
          <Typography variant="caption">Cleaning</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#e4a821" }} />
          <Typography variant="caption">Maintenance</Typography>
        </Box>
      </Box>

      <Paper sx={{ overflow: "auto", flex: 1, minHeight: 465 }}>
        <Box sx={{ display: "flex", flexDirection: "column", minWidth: "max-content" }}>
          <Box sx={{ display: "flex", borderBottom: 1, borderColor: "divider" }} sticky top={0} zIndex={1}>
            <Box sx={{ minWidth: 150, width: 150, flexShrink: 0, p: 1, borderRight: 1, borderColor: "divider", fontWeight: 600, position: "sticky", left: 0, zIndex: 2, bgcolor: "background.paper" }}>Room / Bed</Box>
            {dateRange.map((d) => (
              <Box key={d} sx={{ minWidth: 45, p: 1, fontWeight: 600, verticalAlign: "middle", textAlign: "center", borderRight: 1, borderColor: "divider", fontSize: "0.9rem" }}>
                {dayjs(d).format("DD")}
              </Box>
            ))}
          </Box>
          <Box sx={{ height: "60dvh", display: "flex", flexDirection: "column", minWidth: "max-content" }}>
          {filteredBeds.map((bed) => (
            <Box key={bed.bed_id} sx={{ display: "flex", borderBottom: 1, borderColor: "divider" }}>
              <Box sx={{ minWidth: 150, width: 150, flexShrink: 0, p: 0.5, borderRight: 1, borderColor: "divider", fontSize: "0.8rem", position: "sticky", left: 0, zIndex: 1, bgcolor: "background.paper" }}>
                {bed.room_number?.length > 15 ? <Tooltip title={bed.room_number}><Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.9rem" }}>{bed.room_number.slice(0, 14) + "..."}</Typography></Tooltip>
                  : <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.9rem" }}>{bed.room_number}</Typography>}
                <Typography variant="caption">{bed.bed_number} {bed.room_type || bed.ward_type || ""}</Typography>
              </Box>
              {dateRange.map((dateStr) => {
                const status = getBedDateStatus(bed, dateStr, modifyReservation);
                const selected = isSelected(bed.bed_id, dateStr);
                let bg = "#4CAF50";
                if (status === "booked") bg = "#f44336";
                else if (status === "cleaning") bg = "#2196F3";
                else if (status === "maintenance") bg = "#e4a821";
                if (selected) bg = "#b9ecbb";
                return (
                  <Box
                    key={dateStr}
                    onMouseDown={() => handleCellMouseDown(bed, dateStr)}
                    onMouseEnter={() => handleCellMouseEnter(bed, dateStr)}
                    onMouseUp={() => handleCellMouseUp(bed, dateStr)}
                    sx={{
                      minWidth: 45,
                      height: 47,
                      borderRight: 1,
                      borderBottom: 1,
                      borderColor: "divider",
                      bgcolor: bg,
                      cursor: status === "available" ? "pointer" : "default",
                      userSelect: "none",
                      backgroundImage: status !== "available" ? "repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.3) 6px, rgba(255,255,255,0.3) 6px)" : undefined,
                    }}
                  />
                );
              })}
            </Box>
          ))}
          </Box>
          {filteredBeds.length === 0 && (
            <Box sx={{ p: 2, textAlign: "center" }}>No rooms to display</Box>
          )}
        </Box>
      </Paper>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}
      >
        <Box sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={600}>{modifyReservation ? "Modify Reservation" : "Room Details"}</Typography>
            <IconButton size="small" onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
          </Box>
          <Box mt={2}>
            <Typography fontWeight={600} mb={1}>ROOM DETAILS</Typography>
            <Typography variant="body2">Block/Floor: {selection?.bed?.block_name || "-"}</Typography>
            <Typography variant="body2">Ward: {selection?.bed?.ward_name || "-"}</Typography>
            <Typography variant="body2">Room Type: {selection?.bed?.room_type || "-"}</Typography>
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <Select value={allocatedFor} disabled={!!modifyReservation || !!fromAdmissionDetails} onChange={(e) => setAllocatedFor(e.target.value)}>
                <MenuItem value="Patient">Patient</MenuItem>
                <MenuItem value="House Keeping">House Keeping</MenuItem>
                <MenuItem value="Maintenance">Maintenance</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box mt={2}>
            <Typography fontWeight={600} mb={1}>BOOKING DETAILS</Typography>
            <TextField
              fullWidth
              size="small"
              disabled={!!fromAdmissionDetails || allocatedFor !== "Patient"}
              placeholder="Search by UHID / Mobile No / Name"
              label="Patient UHID / Mobile No / Name"
              value={patientSearchKey}
              onChange={(e) => setPatientSearchKey(e.target.value)}
              onFocus={() => patientSearchKey?.trim() && patientList.length > 0 && setPatientSearchOpen(true)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => patientSearch(patientSearchKey)}><SearchIcon /></IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            {patientSearchOpen && patientList.length > 0 && !fromAdmissionDetails && (
              <Paper sx={{ maxHeight: 220, overflow: "auto", mb: 1 }} elevation={2}>
                {patientList.map((p) => {
                  const { name, line2 } = formatPatientOption(p);
                  return (
                    <Box key={p.patient_id} onClick={() => selectPatient(p)} sx={{ p: 1.5, cursor: "pointer", borderBottom: "1px solid", borderColor: "divider", "&:hover": { bgcolor: "action.hover" } }}>
                      <Typography variant="body2" fontWeight={600}>{name}</Typography>
                      {line2 && <Typography variant="caption" color="text.secondary">{line2}</Typography>}
                    </Box>
                  );
                })}
              </Paper>
            )}
            <TextField fullWidth size="small" label="Patient Name" value={selectedPatient?.patient_name || ""} disabled sx={{ mb: 1 }} />
            {allocatedFor === "Patient" && (
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <Select value={selectedDoctor} onChange={(e) => setSelectedDoctor(e.target.value)} displayEmpty>
                  <MenuItem value="">Select Doctor</MenuItem>
                  {doctorsList.map((doc) => (
                    <MenuItem key={doc.doctor_id} value={doc.doctor_id}>{doc.doctor_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField fullWidth size="small" label="Check In Date & Time" value={selection ? dayjs(selection.startDate).format("DD-MM-YYYY 00:00 AM") : ""} disabled sx={{ mb: 1 }} />
            <TextField fullWidth size="small" label="Check Out Date & Time" value={selection ? dayjs(selection.endDate).format("DD-MM-YYYY 11:59 PM") : ""} disabled sx={{ mb: 1 }} />
            <TextField fullWidth size="small" label="Remarks" inputProps={{ maxLength: 250 }} value={remarks} onChange={(e) => setRemarks(e.target.value)} multiline rows={2} />
          </Box>

          {allocatedFor === "Patient" && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography fontWeight={600} mb={1}>Payment Detail</Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} mb={1}>
                <FormControlLabel control={<Checkbox checked={paymentMode.cash} onChange={() => handlePaymentMode("cash")} />} label="Cash" />
                <FormControlLabel control={<Checkbox checked={paymentMode.card} onChange={() => handlePaymentMode("card")} />} label="Card" />
                <FormControlLabel control={<Checkbox checked={paymentMode.upi} onChange={() => handlePaymentMode("upi")} />} label="UPI" />
                <FormControlLabel control={<Checkbox checked={paymentMode.bank} onChange={() => handlePaymentMode("bank")} />} label="Bank Transfer" />
              </Stack>
              {paymentMode.cash && (
                <TextField label={`Cash Amount (${CurrencySymbol})`} name="cashAmount" value={amounts.cashAmount} onChange={handlePaymentChange} size="small" fullWidth sx={{ mb: 1 }} />
              )}
              {paymentMode.card && (
                <Stack direction="row" spacing={1} mb={1}>
                  <TextField label={`Card Amount (${CurrencySymbol})`} name="cardAmount" value={amounts.cardAmount} onChange={handlePaymentChange} size="small" />
                  <TextField label="Reference" name="cardNumber" value={amounts.cardNumber} onChange={handlePaymentChange} size="small" />
                </Stack>
              )}
              {paymentMode.upi && (
                <Stack direction="row" spacing={1} mb={1}>
                  <TextField label={`UPI Amount (${CurrencySymbol})`} name="upiAmount" value={amounts.upiAmount} onChange={handlePaymentChange} size="small" />
                  <TextField label="Reference" name="upiNumber" value={amounts.upiNumber} onChange={handlePaymentChange} size="small" />
                </Stack>
              )}
              {paymentMode.bank && (
                <Stack direction="row" spacing={1} mb={1}>
                  <TextField label={`Bank Amount (${CurrencySymbol})`} name="bankAmount" value={amounts.bankAmount} onChange={handlePaymentChange} size="small" />
                  <TextField label="Reference" name="bankNumber" value={amounts.bankNumber} onChange={handlePaymentChange} size="small" />
                </Stack>
              )}
            </>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button variant="outlined" fullWidth onClick={() => setDrawerOpen(false)}>CANCEL</Button>
            <Button variant="contained" color="success" fullWidth onClick={validateAndSave}>{modifyReservation ? "UPDATE RESERVATION" : "BOOK ROOM"}</Button>
          </Stack>
        </Box>
      </Drawer>

      {errorMsg && <ToastMsg severity="error" msg={errorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      {loading && <Loader />}
    </Box>
  );
}
